'use client';

import * as React from 'react';

import { cn } from '@/lib/utils';

interface BackdropProps {
  /** Whether the backdrop is visible */
  isVisible: boolean;
  /** Whether the backdrop has animated in (controls opacity) */
  isAnimated?: boolean;
  /** Callback when backdrop is clicked */
  onClick?: () => void;
  /** Whether the backdrop is clickable */
  clickable?: boolean;
  /** Custom z-index for stacking */
  zIndex?: number;
  /** Use absolute positioning instead of fixed */
  absolute?: boolean;
  /** Custom className */
  className?: string;
}

/**
 * Reusable backdrop component with Modern Intimate warm overlay styling.
 * Used by BaseModal and other overlay components.
 *
 * Design notes:
 * - Uses hsl(var(--foreground) / 0.6) for warm, cohesive overlay
 * - Uses backdrop-blur-sm (4px) instead of md (12px) for subtler effect
 * - The warm overlay color does more visual separation than heavy blur
 */
export function Backdrop({
  isVisible,
  isAnimated = true,
  onClick,
  clickable = true,
  zIndex = 50,
  absolute = false,
  className,
}: BackdropProps) {
  const handleClick = React.useCallback(() => {
    if (clickable && onClick) {
      onClick();
    }
  }, [clickable, onClick]);

  if (!isVisible) return null;

  return (
    <div
      role="presentation"
      className={cn(
        'inset-0 backdrop-blur-sm',
        'transition-opacity duration-150',
        absolute ? 'absolute' : 'fixed',
        isAnimated ? 'opacity-100' : 'opacity-0',
        clickable && 'cursor-pointer',
        className
      )}
      style={{
        // DS --foreground at 60% opacity
        backgroundColor: 'hsl(var(--foreground) / 0.6)',
        ...(!absolute && zIndex !== undefined ? { zIndex } : {}),
      }}
      onClick={handleClick}
      aria-hidden="true"
    />
  );
}
